import React, { useEffect, useState } from 'react';
import styles from './style.module.scss';
import { ButtonPrimary } from '@components';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { $pricing, pricingFx, subscribeFx } from '../../features/pricing/model';
import { $user, $userLanguage } from '../../features/app/model';
import { SubscriptionResponseDto } from '../../http/Api';
import ReactGA from 'react-ga4';
import { ReactComponent as StarFilled } from '../../pages/Landing/PricingBlock/assets/StarFilled.svg';
import { ReactComponent as StarOutline } from '../../pages/Landing/PricingBlock/assets/StarOutline.svg';
import { ReactComponent as StarHalf } from '../../pages/Landing/PricingBlock/assets/StarHalf.svg';
import { ReactComponent as TrustpilotLogo } from '../../pages/Landing/PricingBlock/assets/TrustpilotLogo.svg';
import { ReactComponent as AiIcon } from "../../assets/icons/AI.svg";
import { ReactComponent as StickIcon } from "../../assets/icons/Stick.svg";
import { ReactComponent as StarsIcon } from '../../assets/icons/Stars.svg';
import { ReactComponent as PhotoIcon } from "../../assets/icons/PhotoIcon.svg";
import { ReactComponent as CheckIcon } from "../../assets/icons/Check.svg";

interface UpgradePopupProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    subtitle?: string;
    triggerConfetti?: () => void;
}

const PricingCard: React.FC<{
	title: string;
	price: string;
	id: string;
	features: string[];
	colorScheme?: "orange" | "basic";
	discountEnabled: boolean;
	period: string;
	currency: string;
	isSelected: boolean;
	onClick: () => void;
}> = ({
	title,
	price: actualPriceString,
	id,
	period,
	isSelected,
	onClick,
    currency
}) => {
	const { t } = useTranslation();
	const actualPriceNum = Number(actualPriceString);
	let currentDisplayPrice: string;
	let currentDisplayUnitKey: string;
	let originalPriceValueForCalc: number;

	if (period === "year") {
		const monthlyEquivalent = actualPriceNum / 12;
		currentDisplayPrice = monthlyEquivalent.toFixed(2);
		currentDisplayUnitKey = "pricing.month";
		originalPriceValueForCalc = monthlyEquivalent * 2.5;
	} else if (period === "month") {
		currentDisplayPrice = actualPriceNum.toFixed(2);
		currentDisplayUnitKey = "pricing.month";
		originalPriceValueForCalc = actualPriceNum * 2.5;
	} else {
		currentDisplayPrice = actualPriceNum.toFixed(2);
		currentDisplayUnitKey = "pricing.week";
		originalPriceValueForCalc = actualPriceNum * 2.5;
	}

	const finalOriginalDisplayPrice = originalPriceValueForCalc.toFixed(2);

	return (
		<div
			onClick={onClick}
			className={`${styles.PricingCard} ${
				isSelected ? styles.PricingCard__selected : ""
			}`}
		>
			{period === 'month' && (
				<div className={`${styles.PricingCard__badge} ${styles['PricingCard__badge--most-popular']}`}>Most popular</div>
			)}
			{period === 'year' && (
				<>
					<div className={`${styles.PricingCard__badge} ${styles['PricingCard__badge--yellow']}`}>Save 50% 🔥</div>
					<div className={styles.PricingCard__bestValueBadge}>✓ Best value</div>
				</>
			)}
			{period === 'day' && (
				<div className={`${styles.PricingCard__badge} ${styles['PricingCard__badge--weekly']}`}>Weekly plan</div>
			)}

			<div className={styles.PricingCard__header}>
				<div className={styles.PricingCard__selectionIndicator}>
					<div className={isSelected ? styles.PricingCard__selectionIndicator_checked : undefined}></div>
				</div>
				<h3 className={styles.PricingCard__title}>{title}</h3>
			</div>

			<div className={styles.PricingCard__priceContainer}>
				<span className={styles.PricingCard__priceValue}>{currency}{currentDisplayPrice}</span>
				<span className={styles.PricingCard__pricePeriod}>/&nbsp;{t(currentDisplayUnitKey)}</span>
				<span className={styles.PricingCard__originalPrice}>
					{currency}{finalOriginalDisplayPrice}
				</span>
			</div>
			
			{period === "year" && (
				<div className={styles.PricingCard__billedInfo}>
					<span>Billed </span>
					<strong>{`${currency}${(Number(currentDisplayPrice) * 12).toFixed(0)}`}</strong>
					<span> Annually</span>
				</div>
			)}
			{period === "day" && (
				<div className={styles.PricingCard__billedInfo}>
					<span>then $39.99 per month</span>
				</div>
			)}
		</div>
	);
};

export const UpgradePopup: React.FC<UpgradePopupProps> = ({ isOpen, onClose, title, subtitle, triggerConfetti }) => {
    const { t } = useTranslation();
    const [subscriptionsCurrent, setSubscriptionsCurrent] = useState<SubscriptionResponseDto[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionResponseDto | null>(null);
    
    const [data, getSubscriptions] = useUnit([$pricing, pricingFx]);
    const user = useUnit($user);
    const currLang = useUnit($userLanguage);
    const subscribeQuery = useUnit(subscribeFx);

    const rating = 4.6;

    const features: { icon?: JSX.Element; titleKey: string; descriptionKey: string }[] = [
		{ icon: <AiIcon />, titleKey: "pricing.features.detector.title", descriptionKey: "pricing.features.detector.description" },
		{ icon: <StickIcon />, titleKey: "pricing.features.optimizer.title", descriptionKey: "pricing.features.optimizer.description" },
		{ icon: <PhotoIcon />, titleKey: "pricing.features.photoSolver.title", descriptionKey: "pricing.features.photoSolver.description" },
		{ icon: <StarsIcon />, titleKey: "pricing.features.chat.title", descriptionKey: "pricing.features.chat.description" },
	];

	const renderStars = (rating: number) => {
		const stars = [];
		for (let i = 1; i <= 5; i++) {
			if (i <= rating) {
				stars.push(<StarFilled key={i} />);
			} else if (i - rating < 1) {
				stars.push(<StarHalf key={i} />);
			} else {
				stars.push(<StarOutline key={i} />);
			}
		}
		return stars;
	};

    useEffect(() => {
        if (isOpen) {
            getSubscriptions({
                languageCode: currLang?.toUpperCase() || 'EN'
            });
            
            // Google Analytics event for popup open
            ReactGA.event({
                category: "Popup",
                action: "Open",
                label: "Upgrade Popup Shown",
            });

            // Google Ads events
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'upgrade_popup_view',
                    popup_type: 'upgrade',
                    user_email: user?.email,
                });
                window.dataLayer.push({
                    event: 'ads_upgrade',
                    popup_type: 'upgrade',
                    user_email: user?.email,
                });
            }
        }
    }, [isOpen, currLang, getSubscriptions, user?.email]);

    useEffect(() => {
		if (data) {
			const subscriptions = (data as SubscriptionResponseDto[])
				.filter((s) => s.isPricingPage)
				.sort((a, b) => {
					const periodOrder: { [key: string]: number } = { month: 1, day: 2, year: 3 };
					return (periodOrder[a.period] || 99) - (periodOrder[b.period] || 99);
				});
			setSubscriptionsCurrent(subscriptions);
			
			const weeklyPlan = subscriptions.find(s => s.period === 'day');
			if (weeklyPlan) {
				setSelectedPlan(weeklyPlan);
			} else if (subscriptions.length > 0) {
				const defaultPlan = subscriptions.find(s => s.period === 'month') || subscriptions[0];
				setSelectedPlan(defaultPlan);
			}
		}
	}, [data]);

    const handleGetStarted = async () => {
		if (!selectedPlan) return;

		// Google Analytics event for button click
		ReactGA.event({
			category: "Button",
			action: "Click",
			label: "Get access from upgrade popup",
		});

		if (!user || !user.userId) {
			window.location.href = "/auth";
			return;
		}

		const planData = {
			subscriptionId: selectedPlan.subscriptionId,
			title: selectedPlan.title,
			amount: selectedPlan.amount,
			currency: {
				symbol: selectedPlan.currency?.symbol,
				code: 'USD',
			},
		}

		ReactGA.event('initiate_checkout', {
			currency: planData.currency.code,
			value: 0,
			items: [{ item_id: planData.subscriptionId, item_name: planData.title, price: planData.amount }],
		})
		window.dataLayer.push({
			event: 'ads_initiate_checkout',
			value: 0,
			currency: planData.currency.code,
			items: [{ item_id: planData.subscriptionId, item_name: planData.title, price: planData.amount }],
			user_email: user?.email,
		})

		try {
			const orderResponse = await subscribeQuery({
				subscriptionId: selectedPlan.subscriptionId,
				frontendUrl: window.location.origin,
			});

			if (!orderResponse) throw new Error('Failed to create payment session');

			if (orderResponse.sessionUrl) {
				localStorage.setItem('pendingPurchasePlan', JSON.stringify({ ...planData, sessionId: orderResponse.sessionId }));
				window.location.href = orderResponse.sessionUrl;
			} else if (orderResponse.token) {
				const { payWithPopup } = await import("@revolut/checkout").then(m => m.default(
					orderResponse.token!,
					process.env.REACT_APP_REVOLUT_TYPE as "sandbox" | "prod" | "dev",
				));
				payWithPopup({
					onSuccess() {
						const purchaseParams: any = {
							transaction_id: orderResponse.sessionId,
							value: planData.amount,
							currency: planData.currency.code,
							items: [
								{
									item_id: planData.subscriptionId,
									item_name: planData.title,
									price: planData.amount,
								},
							],
						}
						ReactGA.event('purchase', purchaseParams)
						if (window.dataLayer) {
							window.dataLayer.push({
								event: 'ads_purchase',
								transaction_id: purchaseParams.transaction_id,
								value: purchaseParams.value,
								currency: purchaseParams.currency,
								user_email: user?.email,
							})
						}
						// ⭐ НОВОЕ: Mark as tracked BEFORE navigate to prevent App.tsx from sending duplicate
						sessionStorage.setItem('ads_purchase_tracked_new_sub', 'true');
						console.log('🚀 Fired ads_purchase event for Revolut in UpgradePopup');
						// ⭐ конец нового
						window.alert(t('pricing.alert.thanks'))
						window.location.href = '/?new_subscription=true';
					},
					onError(error) {
						console.error('Revolut Popup Error:', error)
						window.alert(`${t("pricing.alert.error")} ${error.toString()}`);
					},
				})
			} else {
				throw new Error("Invalid payment response format from backend");
			}
		} catch (error) {
			console.error("Payment initiation error:", error);
			window.alert(t("pricing.alert.error"));
		}
	};

    if (!isOpen) {
        return null;
    }

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            // Google Analytics event for popup close
            ReactGA.event({
                category: "Popup",
                action: "Close",
                label: "Upgrade Popup Closed - Backdrop Click",
            });

            // Google Ads event
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'upgrade_popup_close',
                    popup_type: 'upgrade',
                    close_reason: 'backdrop_click',
                    user_email: user?.email,
                });
            }
            
            onClose();
        }
    };

    return (
        <div className={styles.overlay} onClick={handleBackdropClick}>
            <div className={styles.modal}>
                <button className={styles.closeButton} onClick={() => {
                    // Google Analytics event for popup close
                    ReactGA.event({
                        category: "Popup",
                        action: "Close",
                        label: "Upgrade Popup Closed - Close Button",
                    });

                    // Google Ads event
                    if (window.dataLayer) {
                        window.dataLayer.push({
                            event: 'upgrade_popup_close',
                            popup_type: 'upgrade',
                            close_reason: 'close_button',
                            user_email: user?.email,
                        });
                    }
                    
                    onClose();
                }}>×</button>
                <div className={styles.header}>
                    <h2 className={styles.title}>{title || "Unlock Premium Features"}</h2>
                    <p className={styles.subtitle}>{subtitle || "Access all advanced tools and unlimited usage to take your content to the next level."}</p>
                </div>
                
                <div className={styles.content}>
                    <div className={styles.Features}>
                        <h3 className={styles.Features__title}>{t("pricing.allPlansInclude")}</h3>
                        <div className={styles.Features__list}>
                            {features.map((feature, index) => (
                                <div key={index} className={styles.Features__item}>
                                    <div className={styles.Features__iconWrapper}>{feature.icon}</div>
                                    <div className={styles.Features__textWrapper}>
                                        <h4>{t(feature.titleKey)}</h4>
                                        <span>{t(feature.descriptionKey)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.TrustpilotWrapper}>
                        <a href="https://www.trustpilot.com/review/humanizeai.now" target="_blank" rel="noopener noreferrer" className={styles.TrustpilotReview}>
                            <span>Excellent</span>
                            <div className={styles.TrustpilotStars}>
                                {renderStars(rating)}
                            </div>
                            <div className={styles.TrustpilotLogoContainer}>
                                <TrustpilotLogo />
                                <span>Trustpilot</span>
                            </div>
                        </a>
                    </div>

                    <div className={styles.plansContainer}>
                        {subscriptionsCurrent?.length
                            ? subscriptionsCurrent.map((el: SubscriptionResponseDto) => (
                                <PricingCard
                                    key={el.subscriptionId}
                                    discountEnabled={el.period === 'month'}
                                    title={el.title || ""}
                                    price={el.amount ? el.amount.toFixed(2) : "0.00"}
                                    id={el.subscriptionId}
                                    period={el.period}
                                    features={el.features || []}
                                    currency={el.currency?.symbol || ""}
                                    isSelected={selectedPlan?.subscriptionId === el.subscriptionId}
                                    onClick={() => {
                                        setSelectedPlan(el);
                                        
                                        // Google Analytics event for plan selection
                                        ReactGA.event({
                                            category: "Plan",
                                            action: "Select",
                                            label: `${el.title} - ${el.period}`,
                                        });

                                        // Google Ads event
                                        if (window.dataLayer) {
                                            window.dataLayer.push({
                                                event: 'plan_selected',
                                                plan_type: el.period,
                                                plan_name: el.title,
                                                plan_price: el.amount,
                                                currency: el.currency?.code,
                                                user_email: user?.email,
                                            });
                                        }
                                    }}
                                />
                            ))
                            : "No pricing plans available"}
                    </div>

                    <div className={styles.getStartedButtonContainer}>
                        <ButtonPrimary
                            onClick={handleGetStarted}
                            disabled={!selectedPlan}
                            colorScheme="orange"
                            textAlign="center"
                            className={styles.getStartedButton}
                        >
                            Get Started
                        </ButtonPrimary>
                    </div>
                    
                    <div className={styles.Guarantees}>
                        <span><CheckIcon /> Instant Access</span>
                        <span><CheckIcon /> Cancel Anytime</span>
                        <span><CheckIcon /> 30-day Refund Guarantee</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
